import React, { Fragment } from "react"

import { useTranslation } from "react-i18next"

import { Box, Grid, Skeleton } from "@mui/material"
import { Button, Typography } from "@synapse-analytics/synapse-ui"

import type { MetricsState } from "../../types/custom/projects"
import { InfoContainer } from "../core/InfoContainer"
import { GraphHeader, type GraphHeaderProps } from "./GraphHeader"

export interface ChartsContainerProps extends GraphHeaderProps {
  isLoading: boolean
  emptyState?: MetricsState
  graphHeight?: number | string
  children: React.ReactElement
  gutterBottom?: boolean
  reportType?: "simulation"
}

/**
 * Graph container to encapsulate (loading, empty, rendering ,...ext)  states
 * @return {React.ReactElement}
 */
export function ChartsContainer(props: Readonly<ChartsContainerProps>): React.ReactElement {
  const { t } = useTranslation("graphs")

  const {
    graphHeight = 400,
    title,
    range,
    tooltipText,
    isLoading,
    emptyState,
    children,
    selectProps,
    headerMode = "full",
    customHeaderComponent,
    gutterBottom,
    reportType,
  } = props

  return (
    <div
      className={"charts-paper"}
      style={
        reportType === "simulation"
          ? { padding: "1rem", display: "flex", flexDirection: "column", justifyContent: "space-between" }
          : { padding: "1rem" }
      }
    >
      {/* hide header when the mode is "select" and the data is still loading */}
      {!(isLoading && headerMode === "select") && (
        <GraphHeader
          title={title}
          tooltipText={tooltipText}
          range={range}
          selectProps={selectProps}
          headerMode={headerMode}
          customHeaderComponent={customHeaderComponent}
          gutterBottom={gutterBottom}
        />
      )}

      <Box display={"flex"} style={{ height: graphHeight, padding: isLoading ? "0px 16px" : "0px" }}>
        {isLoading ? (
          // Loading skeletons
          <Grid container direction="column" justifyContent="center" alignItems="flex-start" className={"full-height"}>
            <Skeleton animation="wave" width="100%" height={40} />
            <Skeleton animation="wave" width="75%" />
            <Skeleton animation="wave" width="60%" height={30} />
            <Skeleton animation="wave" width="30%" />
          </Grid>
        ) : emptyState ? (
          <Grid container direction="row" justifyContent="center" alignItems="center" className={"full-height"}>
            {emptyState?.title ? (
              <InfoContainer
                title={emptyState?.title}
                subtitle={emptyState?.description}
                action={emptyState?.action ? <Button variant="primary">{emptyState?.action}</Button> : undefined}
              />
            ) : (
              <Typography variant="p">{t("chartsContainer.noPredictionsOrFeedback")}</Typography>
            )}
          </Grid>
        ) : (
          <Fragment>{children}</Fragment>
        )}
      </Box>
    </div>
  )
}
